import React, { useState, useEffect } from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { a11yLight } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import { getConfig } from 'functions/getConfig';
import { Languages } from 'constants/languages';
import TimelineGraph from './timelineGraph';
import TimelineGraphPasteEvent from './timelineGraphPasteEvent';
import TimelineGraphTimeAwayEvent from './timelineGraphTimeAwayEvent';
import TimelineGraphCopyEvent from './timelineGraphCopyEvent';

import {
  ActiveTabBox,
  Heading,
  HeadingUnderLine,
  FlexContainer,
  InnerBorderPrintContainer,
  BorderPrintContainer,
} from '../styles';
import {
  TestCodeButton,
  CodeWrittenText,
  CodeContainer,
  FlexParent,
  DescriptionText,
  BoldText,
  OvertimeTextDescription,
  CopiedTextContainer,
} from './styles';
import { Checkbox } from 'components/Checkbox/Checkbox';

export default React.memo(({ codeInputs, ticket, bestRunInput, anonymiseReportId, setDisplayedInput }) => {
  const config = getConfig();
  const inputs = codeInputs;
  const [selectedInput, setSelectedInput] = useState(bestRunInput);
  const [code, setCode] = useState(bestRunInput.code);
  const [showPastedTimeline, setShowPastedTimeline] = useState(false);
  const [showFullOverTime, setShowFullOverTime] = useState(false);
  const [pastedHighlightArea, setPastedHighlightArea] = useState([0, 0]);
  const [copiedText, setCopiedText] = useState({});

  useEffect(() => {
    if (!selectedInput.pastedText) {
      setDisplayedInput(selectedInput);
      setShowPastedTimeline(false);
    } else {
      setShowPastedTimeline(true);
    }
  }, [selectedInput, showPastedTimeline, setDisplayedInput]);

  useEffect(() => {
    // if we have selected an overtime input and we are not showing the full overtime graph
    // we want to select the best run input to display instead
    if (selectedInput.afterDeadline && !selectedInput.isBestAfterDeadline && !showFullOverTime) {
      let input = inputs.find((i) => i.isBestRun);
      setSelectedInput(input);
      setCode(input.code);
    }
  }, [showFullOverTime, selectedInput.afterDeadline, selectedInput.isBestAfterDeadline, inputs]);

  const scrollToSection = (id = 'code') => {
    let element = document.getElementById(id);
    if (!element) return;
    let headerOffset = 60;
    let elementPosition = element.getBoundingClientRect().top;
    let offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  const setHighlightIndexesForPastedTexts = (input) => {
    if (!input.pastedText) return setPastedHighlightArea([0, 0]);

    const pastedText = input.pastedText.trim();

    // find index of start of pasted code. use replace to escape regex characters and then replace whitespace with regex for any whitespace
    const indexOfStartOfPastedCode =
      input.code.match(pastedText.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&').replace(/\s+/gm, '\\s*'))?.index ?? -1;

    if (indexOfStartOfPastedCode === -1) return setPastedHighlightArea([0, 0]);

    // find line number from indexOfStartOfPastedCode
    const lineNumberPastedCodeStart = input.code.substr(0, indexOfStartOfPastedCode).split(/\n/gm).length;
    const lineNumberPastedCodeEnd = lineNumberPastedCodeStart + pastedText.split(/\n/gm).length - 1;

    setPastedHighlightArea([lineNumberPastedCodeStart, lineNumberPastedCodeEnd]);
  };

  return (
    <ActiveTabBox forPrint={true} forCode={true}>
      <BorderPrintContainer>
        <InnerBorderPrintContainer>
          <Heading>CODE</Heading>
          <HeadingUnderLine />
          <FlexContainer alignItemsCenter>
            <CodeWrittenText id="code">
              Code written in {selectedInput?.languageVersion || Languages[selectedInput?.language]?.SimpleName}
            </CodeWrittenText>
            <FlexContainer handleButtonsOnSmallDevice>
              {!showPastedTimeline && (
                <TestCodeButton href={`${config.REACT_URL}/freecode-test-inspect/${selectedInput._id}`} target="_blank">
                  <i className="fa fa-edit"></i>
                  {` `}Test Code
                </TestCodeButton>
              )}
              {!showPastedTimeline && (
                <TestCodeButton
                  href={`${config.REACT_URL}/codeComparison/${ticket?.anonymiseId ? ticket?.anonymiseId : ticket._id}/${
                    ticket?.anonymiseId ? true : false
                  }`}
                  target="_blank"
                >
                  <i className="fa fa-magnifying-glass"></i>
                  {` `}Compare Code
                </TestCodeButton>
              )}
            </FlexContainer>
          </FlexContainer>
          <CodeContainer>
            <SyntaxHighlighter
              lineProps={(lineNumber) => {
                let style = { display: 'block' };
                const [start, end] = pastedHighlightArea;
                if (lineNumber >= start && lineNumber <= end) {
                  style.backgroundColor = '#f1c40f80';
                }
                return { style };
              }}
              // The library require uses a different name for c# then we do in editorNames
              language={selectedInput.language === 'c_sharp' ? 'csharp' : selectedInput.language}
              style={a11yLight}
              wrapLines={true}
              showLineNumbers={true} // needed for lineNumbers but disabled through css
            >
              {code}
            </SyntaxHighlighter>
          </CodeContainer>
          {selectedInput.pastedText && (
            <>
              <CodeWrittenText>Pasted Text</CodeWrittenText>
              <CodeContainer>
                <SyntaxHighlighter
                  language={selectedInput.language === 'c_sharp' ? 'csharp' : selectedInput.language}
                  style={a11yLight}
                >
                  {selectedInput.pastedText}
                </SyntaxHighlighter>
              </CodeContainer>
            </>
          )}
          {copiedText.data && (
            <>
              <CodeWrittenText id="copiedText">
                Copied Text (From: {copiedText.type.charAt(0).toUpperCase() + copiedText.type.slice(1)})
              </CodeWrittenText>
              {copiedText.type === 'description' ? (
                <CopiedTextContainer>{copiedText.data}</CopiedTextContainer>
              ) : (
                <CopiedTextContainer>
                  <SyntaxHighlighter
                    language={selectedInput.language === 'c_sharp' ? 'csharp' : selectedInput.language}
                    style={a11yLight}
                  >
                    {copiedText.data}
                  </SyntaxHighlighter>
                </CopiedTextContainer>
              )}
            </>
          )}

          <FlexContainer marginTop={'20px'} handleSmallDevice>
            <Heading> TIMELINE</Heading>
            {ticket.extendedDeadline && inputs.filter((i) => i.afterDeadline).length > 1 && (
              <FlexContainer marginBottom={'10px'} hidePrint={true}>
                <Checkbox
                  checked={showFullOverTime}
                  onChange={() => {
                    setShowFullOverTime(!showFullOverTime);
                  }}
                  label="Show full overtime graph"
                />
              </FlexContainer>
            )}
          </FlexContainer>

          <FlexParent isLongPastedTimeline={showPastedTimeline && inputs.length > 15}>
            <div>
              <TimelineGraph
                key={ticket._id + showFullOverTime}
                ticket={ticket}
                inputs={inputs.filter((i) => !i.afterDeadline)}
                afterDeadlineInputs={inputs.filter((i) => i.afterDeadline && !i.isBestAfterDeadline)}
                bestOverTimeInput={inputs.find((i) => i.isBestAfterDeadline)}
                selectedInput={selectedInput}
                showFullOverTime={showFullOverTime}
                setCurrentInput={(input) => {
                  setHighlightIndexesForPastedTexts(input);
                  setSelectedInput(input);
                  setCode(input.code);
                  setCopiedText({});
                  scrollToSection();
                }}
              />

              {ticket.copyEvents?.length > 0 && (
                <TimelineGraphCopyEvent
                  ticket={ticket}
                  inputs={inputs}
                  copyEvents={ticket.copyEvents}
                  showFullOverTime={showFullOverTime}
                  setCopiedText={(data) => {
                    setCopiedText(data);
                    setTimeout(() => {
                      scrollToSection('copiedText');
                    }, 0);
                  }}
                />
              )}

              {ticket?.timeAway?.length > 0 && (
                <TimelineGraphTimeAwayEvent ticket={ticket} inputs={inputs} showFullOverTime={showFullOverTime} />
              )}

              {ticket.pastedTextsInputs.length > 0 && (
                <TimelineGraphPasteEvent
                  ticket={ticket}
                  inputs={inputs}
                  selectedInput={selectedInput}
                  pastedTextsInputs={ticket.pastedTextsInputs}
                  showFullOverTime={showFullOverTime}
                  setCurrentInput={(input) => {
                    setHighlightIndexesForPastedTexts(input);
                    setSelectedInput(input);
                    setCode(input.code);
                    setCopiedText({});
                    scrollToSection();
                  }}
                />
              )}
            </div>
          </FlexParent>

          <>
            <DescriptionText>
              Each dot represents a point in time during the test, hover on a dot to see the score and time of run.
              {ticket.bestRun ? ' The best scoring code is marked with "BEST RUN"' : ''}.
              <BoldText> Click on a dot to display the code at that point!</BoldText>
              {ticket.isOvertime && (
                <OvertimeTextDescription>
                  <i className="fa fa-info-circle"></i>
                  <BoldText className="overtime-text prompt">&nbsp;Overtime</BoldText> - The official result is the one
                  achieved within the time limit
                </OvertimeTextDescription>
              )}
            </DescriptionText>
          </>
        </InnerBorderPrintContainer>
      </BorderPrintContainer>
    </ActiveTabBox>
  );
});
